import React from 'react';

import ZoomButton from '../zoom-button/zoom-button';
import { Box, useMediaQuery } from '@mui/material';
import { getIsZoomEnabled, zoomEnabledSet } from '../../store/car-slice';
import { useDispatch, useSelector } from 'react-redux';
import { getTouchEventData } from '../../utils/get-touch-event';
import FabControlled from '../fab/fab-controlled';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { galleryZoomStepSet } from '../../store/gallery-slice';

type Transform = {
  x: number;
  y: number;
  scale: number;
};

type PropTypes = {
  children: React.ReactNode;
  initialScale?: number;
  zoomWidthButton?: boolean;
  onFullscreen?: () => void;
  zoomStep?: number;
  fullscreen?: boolean;
  section: 'exterior-web' | 'exterior-mobile' | 'interior' | 'gallery';
  objectFit?: string;
};

// TODO: refactor this file

export default function Zoom(props: PropTypes) {
  const {
    children,
    initialScale = 1,
    section,
    onFullscreen,
    fullscreen = false,
    zoomStep,
    objectFit = 'cover',
  } = props;
  // Refs

  const imageRef = React.useRef<HTMLImageElement>(null);

  const isPortrait = useMediaQuery('(orientation: portrait)');

  const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down('lg'));

  // States
  const [initialPosition, setInitialPosition] = React.useState({ x: 0, y: 0 });
  const [initialMousePos, setInitialMousePos] = React.useState({ x: 0, y: 0 });
  const [position, setPosition] = React.useState({ x: 0, y: 0 });
  const [isDrag, setIsDrag] = React.useState(false);
  const [scale, setScale] = React.useState(initialScale);
  // Selectors
  const isZoomEnabled = useSelector(getIsZoomEnabled);

  const dispatch = useDispatch();

  // helper functions
  const make3dTransformValue = (value: any) => {
    const { x, y, scale } = value;
    return `scale3d(${scale}, ${scale}, 1) translate3d(${x}px, ${y}px, 0)`;
  };

  const getMaximumXAndY = () => {
    let maxX = 0;
    let maxY = 0;

    if (imageRef?.current?.clientHeight && imageRef?.current?.clientWidth) {
      maxX = (imageRef.current.clientWidth * scale - window.innerWidth) / scale;
      maxY =
        (imageRef.current.clientHeight * scale - window.innerHeight) / scale;
    }

    return { maxX, maxY };
  };

  const getCenterCoordinates = () => {
    const { maxX, maxY } = getMaximumXAndY();

    const centerX = -(maxX / 2);
    const centerY = -(maxY / 2);

    return { centerX, centerY };
  };

  const updateImagePosition = ({ x, y, scale }: Transform) => {
    const value = make3dTransformValue({
      x,
      y,
      scale,
    });

    imageRef?.current?.style?.setProperty('transform', value);
  };

  React.useEffect(() => {
    if (scale === 1) {
      dispatch(zoomEnabledSet(false));
    } else if (scale > 1) {
      dispatch(zoomEnabledSet(true));

      const { centerX } = getCenterCoordinates();

      setPosition({
        x: centerX,
        y: 0,
      });

      updateImagePosition({ x: centerX, y: 0, scale: initialScale });
    }
  }, []);

  //   mouse and touch events
  const onTouchStart = (e: TouchEvent | MouseEvent) => {
    e.preventDefault();

    const clientX = getTouchEventData(e).clientX;
    const clientY = getTouchEventData(e).clientY;

    setIsDrag(true);

    setInitialMousePos({ x: clientX, y: clientY });

    setInitialPosition({ x: position.x, y: position.y });
  };

  // Common event handler for both mouse and touch events
  const handleMove = (e: TouchEvent | MouseEvent) => {
    if (!isDrag || !isZoomEnabled) return;

    const clientX = getTouchEventData(e).clientX;
    const clientY = getTouchEventData(e).clientY;

    const deltaX = clientX - initialMousePos.x;
    const deltaY = clientY - initialMousePos.y;

    const { maxX, maxY } = getMaximumXAndY();

    let finalX = initialPosition.x + deltaX;
    let finalY = initialPosition.y + deltaY;

    // Set x limit
    if (finalX <= -maxX) {
      finalX = -maxX;
    } else if (finalX >= 0) {
      finalX = 0;
    }

    // Set y limit
    if (finalY <= -maxY) {
      finalY = -maxY;
    } else if (finalY >= 0) {
      finalY = 0;
    }

    setPosition({
      x: finalX,
      y: finalY,
    });

    const transformedY = isPortrait && isMobile && isZoomEnabled ? 0 : finalY;

    updateImagePosition({ x: position.x, y: transformedY, scale });
  };

  const handleMouseUp = (e: TouchEvent | MouseEvent) => {
    e.preventDefault();

    setIsDrag(false);
  };

  // zoom in
  const onZoomIn = () => {
    dispatch(zoomEnabledSet(true));

    let currentScale = 0;

    if (isPortrait && isMobile) {
      currentScale = 2;
      setScale(2);
    } else {
      currentScale = 2;
      setScale(2);
    }

    const { centerX, centerY } = getCenterCoordinates();

    setPosition({
      x: centerX,
      y: centerY,
    });

    const transformedY = isPortrait && isMobile ? 0 : centerY;

    updateImagePosition({
      x: centerX,
      y: transformedY,
      scale: currentScale,
    });
  };

  // zoom out
  const onZoomOut = () => {
    dispatch(zoomEnabledSet(false));
    setScale(1);

    updateImagePosition({
      x: 0,
      y: 0,
      scale: 1,
    });
  };

  const handleDoubleClick = () => {
    if (isMobile) return;

    if (isZoomEnabled) onZoomOut();
    else onZoomIn();
  };

  const handleClickZoom = () => {
    if (isZoomEnabled) onZoomOut();
    else {
      if (onFullscreen) onFullscreen();
      onZoomIn();
      return;
    }
  };

  const handleClickZoomInGallery = () => {
    if (zoomStep === 2 && !isZoomEnabled) {
      onZoomIn();
    }
    if (onFullscreen) onFullscreen();
  };

  const handleClickZoomOutGallery = () => {
    if (zoomStep === 2 && isZoomEnabled) {
      onZoomOut();
    } else onZoomOut();
  };

  const handleClose = () => {
    dispatch(galleryZoomStepSet(1));
    dispatch(zoomEnabledSet(false));

    updateImagePosition({
      x: 0,
      y: 0,
      scale: 1,
    });
  };

  return (
    <div
      className="animate-fade"
      style={{
        position: fullscreen ? 'fixed' : 'initial',
        top: 0,
        left: 0,
        overflow: 'hidden',
        touchAction: 'none',
        width: isPortrait && isMobile && isZoomEnabled ? 'auto' : '100%',
        height: '100%',
        transition: 'all 0.3s ease-in-out',
        display: zoomStep === 1 ? 'contents' : 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        background: zoomStep === 2 ? '#454545' : 'none',
      }}
    >
      {React.cloneElement(children as React.ReactElement, {
        ref: imageRef,
        style: {
          display: zoomStep && zoomStep === 1 ? 'none' : 'block',
          height: isPortrait && isMobile ? 'auto' : '100%',
          width: '100%',
          //! image is not zoom in center position
          transformOrigin:
            isPortrait && isMobile && isZoomEnabled ? '0' : '0 0',
          objectFit: objectFit,
          cursor: isZoomEnabled ? 'grab' : 'zoom-in',
          transition: 'all 0.1s linear',
          borderRadius: zoomStep === 1 ? '10px' : '0px',
        },
        onMouseDown: onTouchStart,
        onMouseMove: handleMove,
        onTouchMove: handleMove,
        onTouchStart: onTouchStart,
        onTouchEnd: handleMouseUp,
        onMouseUp: handleMouseUp,
        onDoubleClick: handleDoubleClick,
      })}

      <ZoomButton
        handleClickZoom={handleClickZoom}
        handleClickZoomIn={
          section === 'gallery' ? handleClickZoomInGallery : undefined
        }
        handleClickZoomOut={
          section === 'gallery' ? handleClickZoomOutGallery : undefined
        }
        isZoomEnabled={isZoomEnabled}
        section={section}
        zoomStep={zoomStep}
      />

      {/* this button only display in zoom step 2 of gallery */}
      {zoomStep === 2 && (
        <Box
          sx={{
            position: 'absolute',
            right: '2rem',
            '@media(max-width: 1000px)': {
              right: isPortrait ? '1rem' : '1.5rem',
            },
            top: '1rem',
            zIndex: 99999,
          }}
        >
          <FabControlled Icon={CloseRoundedIcon} onClick={handleClose} />
        </Box>
      )}
    </div>
  );
}
